import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Check, X, ChevronRight } from "lucide-react";

/**
 * Fase 8.3 · Pannello di approvazione dei dati AI proposti su un vino.
 *
 * Props:
 *  - wineId: id del vino
 *  - aiProposed: oggetto {data, confidence, generated_at, generated_by} oppure null
 *  - currentValues: valori correnti del vino (per il diff campo per campo)
 *  - onChanged: callback dopo apply/reject/rerun (per ricaricare la scheda)
 */
export default function AIProposedPanel({ wineId, aiProposed, currentValues, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState(() => new Set());

    // Se non c'è aiProposed, rendiamo solo il pulsante "Arricchisci con AI".
    if (!aiProposed || !aiProposed.data) {
        return (
            <div className="bg-white border border-dashed border-[hsl(var(--border))] rounded-sm p-4 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-sm font-medium mb-1">Arricchimento AI</div>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        Rilancia l'AI per proporre paese/regione/vitigni/denominazione.
                        Nome, produttore e annata sono <b>immutabili</b>.
                    </p>
                </div>
                <RerunAIButton
                    wineId={wineId}
                    busy={busy}
                    setBusy={setBusy}
                    onChanged={onChanged}
                />
            </div>
        );
    }

    const entries = Object.entries(aiProposed.data)
        .filter(([, v]) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0));

    const toggle = (field) => {
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(field)) next.delete(field); else next.add(field);
            return next;
        });
    };
    const selectAll = () => {
        setSelected(new Set(entries.map(([k]) => k)));
    };
    const clearSel = () => setSelected(new Set());

    const applySelected = async () => {
        if (selected.size === 0) return;
        setBusy(true);
        try {
            await api.post(`/admin/wines/${wineId}/ai-proposed/accept`, {
                fields: Array.from(selected),
            });
            toast.success(`Applicati ${selected.size} campi`);
            clearSel();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore apply");
        } finally { setBusy(false); }
    };

    const applyAll = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/wines/${wineId}/ai-proposed/accept`, { fields: [] });
            toast.success("Applicate tutte le proposte AI");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore apply");
        } finally { setBusy(false); }
    };

    const rejectAll = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/wines/${wineId}/ai-proposed/reject`);
            toast.success("Proposte AI scartate");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore reject");
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-[#FDFBF7] border border-[#D9B98F] rounded-sm p-4" data-testid="ai-proposed-panel">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                    <h3 className="text-base font-medium">Dati AI proposti · in attesa di approvazione</h3>
                    {aiProposed.confidence && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#F4E9DC] text-[#5B1C2C] border border-[#D9B98F]">
                            Confidenza {aiProposed.confidence}
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={selectAll}
                        className="co-btn-ghost text-xs"
                        data-testid="aip-select-all"
                    >
                        Seleziona tutti
                    </button>
                    <RerunAIButton
                        wineId={wineId}
                        busy={busy}
                        setBusy={setBusy}
                        onChanged={onChanged}
                        variant="ghost"
                    />
                </div>
            </div>
            <ul className="space-y-2">
                {entries.map(([field, proposedVal]) => {
                    const currentVal = getCurrent(currentValues, field);
                    return (
                        <li
                            key={field}
                            className={`p-2 rounded-sm border ${selected.has(field) ? "border-[hsl(var(--primary))] bg-white" : "border-transparent bg-white"}`}
                            data-testid={`aip-field-${field}`}
                        >
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selected.has(field)}
                                    onChange={() => toggle(field)}
                                    className="mt-1"
                                    data-testid={`aip-check-${field}`}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1">
                                        {FIELD_LABELS[field] || field}
                                    </div>
                                    <div className="flex items-start gap-2 text-sm flex-wrap">
                                        <span className="text-[hsl(var(--muted-foreground))] line-clamp-2">
                                            {formatVal(currentVal) || <em className="text-[hsl(var(--muted-foreground))]">(vuoto)</em>}
                                        </span>
                                        <ChevronRight className="w-4 h-4 mt-0.5 text-[hsl(var(--muted-foreground))] shrink-0" strokeWidth={1.5} />
                                        <span className="text-[hsl(var(--foreground))] line-clamp-3">
                                            {formatVal(proposedVal)}
                                        </span>
                                    </div>
                                </div>
                            </label>
                        </li>
                    );
                })}
            </ul>
            <div className="mt-4 pt-3 border-t border-[hsl(var(--border))] flex gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={applySelected}
                    disabled={busy || selected.size === 0}
                    className="co-btn-primary text-xs"
                    data-testid="aip-apply-selected"
                >
                    <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Applica selezionati ({selected.size})
                </button>
                <button
                    type="button"
                    onClick={applyAll}
                    disabled={busy}
                    className="co-btn-ghost text-xs"
                    data-testid="aip-apply-all"
                >
                    Applica tutti
                </button>
                <button
                    type="button"
                    onClick={rejectAll}
                    disabled={busy}
                    className="co-btn-ghost text-xs text-[hsl(var(--destructive))]"
                    data-testid="aip-reject-all"
                >
                    <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Scarta proposta
                </button>
            </div>
        </div>
    );
}

function RerunAIButton({ wineId, busy, setBusy, onChanged, variant = "primary" }) {
    const cls = variant === "ghost" ? "co-btn-ghost text-xs" : "co-btn-primary text-xs";
    const run = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/wines/${wineId}/ai-propose`);
            toast.success("Nuova proposta AI generata");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore AI");
        } finally { setBusy(false); }
    };
    return (
        <button
            type="button"
            onClick={run}
            disabled={busy}
            className={cls}
            data-testid="aip-rerun-ai"
        >
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
            {busy ? "In corso…" : "Arricchisci con AI"}
        </button>
    );
}

const FIELD_LABELS = {
    country: "Paese",
    region: "Regione",
    provincia: "Provincia",
    denomination: "Denominazione",
    grapes: "Vitigni",
    type: "Tipologia",
    tasting_notes: "Note di degustazione",
    pairing: "Abbinamenti",
    description: "Descrizione",
};

function getCurrent(currentValues, field) {
    if (!currentValues) return "";
    if (field === "grapes") return (currentValues.organoleptic?.grapes || []).join(", ");
    if (["tasting_notes", "pairing", "description"].includes(field)) {
        return currentValues.organoleptic?.[field] || "";
    }
    return currentValues[field] || "";
}

function formatVal(v) {
    if (v == null) return "";
    if (Array.isArray(v)) return v.join(", ");
    return String(v);
}
